import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { Building2, MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { PRIMARY, Overline, AuroraLight, Reveal } from "../components/shared/kursusUi";
import { LOCATION_PAGES } from "../data/locationPages";
import { CONTACT, PARTNER_VENUES, VOTRA_LOCATIONS } from "../data/votraLocations";

const LocationsPage = () => (
  <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
    <div className="votra-grain" aria-hidden />
    <Seo
      title="Votras lokationer i Søborg og Ringsted | Votra"
      description="Votra har lokationer i Søborg (hovedkontor) og Ringsted. Se hvad der foregår på hver adresse, og hvor vognmandsprøverne afholdes."
      path="/lokationer"
      jsonLd={[
        breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Lokationer", path: "/lokationer" }]),
      ]}
    />
    <Header />

    <section className="hero-light relative overflow-hidden" style={{ backgroundColor: "#F4F6FB" }}>
      <AuroraLight />
      <div className="fx-scan"><span /></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10 py-16 md:py-24 text-center">
        <Reveal i={1} className="flex justify-center"><Overline light>Find vej</Overline></Reveal>
        <Reveal i={2}>
          <h1 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight mb-5">
            Votras lokationer
          </h1>
        </Reveal>
        <Reveal i={3}>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
            {CONTACT.legalName} har egne lokationer i Søborg og Ringsted. Vognmandsprøverne afholdes hos os,
            og Votra er godkendt prøveudbyder hos Færdselsstyrelsen.
          </p>
        </Reveal>
      </div>
    </section>

    <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
      <div className="max-w-5xl mx-auto">
        <div className="grid gap-6 md:grid-cols-2">
          {LOCATION_PAGES.map((p, i) => {
            const addresses = VOTRA_LOCATIONS.filter((l) => p.locationIds.includes(l.id));
            return (
              <Reveal
                i={i}
                key={p.slug}
                className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8 shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(15,40,90,0.13)]"
                data-testid={`location-card-${p.slug}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {addresses.some((a) => a.hq) && (
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: "hsl(210, 100%, 45%, 0.1)", color: PRIMARY }}
                    >
                      <Building2 className="h-3.5 w-3.5" /> Hovedkontor
                    </span>
                  )}
                  {p.isExamVenue && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                      <ShieldCheck className="h-3.5 w-3.5" /> Prøvested
                    </span>
                  )}
                </div>
                <h2 className="font-display text-2xl font-medium text-slate-900">{p.title}</h2>
                <div className="mt-3 space-y-1.5">
                  {addresses.map((a) => (
                    <p key={a.id} className="flex items-start gap-2 text-slate-600">
                      <MapPin className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: PRIMARY }} />
                      <span>{a.street}, {a.postalCode} {a.city}</span>
                    </p>
                  ))}
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">{p.summary}</p>
                <Link
                  to={`/lokationer/${p.slug}`}
                  className="mt-5 inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all"
                  style={{ color: PRIMARY }}
                  data-testid={`location-link-${p.slug}`}
                >
                  Se {p.title} <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-12 rounded-2xl border border-slate-200 bg-white p-6" data-testid="partner-venues">
          <Overline>Samarbejdspartner</Overline>
          <h2 className="font-display text-2xl font-medium text-slate-900 mt-1 mb-3">
            Undervisning hos UCplus
          </h2>
          <p className="text-slate-600 leading-relaxed">
            AMU-forløbene udbydes gennem vores samarbejdspartner UCplus, der også afholder undervisningen.
            Disse adresser er UCplus' lokaler, ikke Votras egne forretningsadresser:
          </p>
          <ul className="mt-3 space-y-1.5 text-slate-600">
            {PARTNER_VENUES.map((v) => (
              <li key={v.id} className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-1 flex-shrink-0 text-slate-400" />
                <span>{v.name}, {v.street}, {v.postalCode} {v.city}</span>
              </li>
            ))}
          </ul>
          <Link to="/adresser" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: PRIMARY }}>
            Se alle adresser med kort <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>

    <Footer />
  </div>
);

export default LocationsPage;
